import { useState } from "react";
import i18next from "../../i18n";
import { BIDDING_TYPES } from "../../utils";
import { OrderToSubmitRequest } from "../../types";
import {
  useCreateOrder,
  useCreateMultipleOrder,
  useUpdateOrder,
} from "../../hooks/useOrders";
import { buildRequestOrderPayload } from "./buildRequestOrderPayload";

type RequestOrderSubmissionArgs = {
  ordersToSubmit: OrderToSubmitRequest[];
  generalBidData: any;
  selectedType: any;
  selectedComplexType: any;
  user: any;
  invites: any[];
  cleanUserInvites: () => void;
  createOrderInvitation: (emailsList: any, orderId: any) => void;
  openNotification: (message: string) => void;
  openError: (error: unknown) => void;
  navigate: (to: string) => void;
  closeDialog: () => void;
  reset: () => void;
};

/**
 * The launch-from-PR create/edit mutations + launch orchestration (single vs
 * joint vs one-order-per-line, plus the `PUT /order/{id}` edit path), lifted
 * verbatim from OrderByRequest.tsx. The payload comes from
 * `buildRequestOrderPayload` (the byte-equivalent SSOT). Distinct from
 * Order/useOrderSubmission because of the edit (update) path and the
 * launch-flow's own notification/invitation bookkeeping.
 */
export const useRequestOrderSubmission = ({
  ordersToSubmit,
  generalBidData,
  selectedType,
  selectedComplexType,
  user,
  invites,
  cleanUserInvites,
  createOrderInvitation,
  openNotification,
  openError,
  navigate,
  closeDialog,
  reset,
}: RequestOrderSubmissionArgs) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatingIndividualOrders, setCreatingIndividualOrders] =
    useState(false);
  const [individualOrdersCompleted, setIndividualOrdersCompleted] = useState(0);
  const [totalIndividualOrders, setTotalIndividualOrders] = useState(0);
  const [pendingOrderInvitations, setPendingOrderInvitations] = useState<any[]>(
    []
  );

  const orderInvitationHandler = (isMultiple: boolean, data: any) => {
    if (invites.length === 0) return;
    if (isMultiple) {
      createOrderInvitation(invites[0].emailsList, data[0].id);
    } else {
      createOrderInvitation(invites[0].emailsList, data.id);
    }
    cleanUserInvites();
  };

  const { mutate: createOrder, isPending: isCreatingOrder } = useCreateOrder(
    (res) => {
      openNotification(i18next.t("tenders.processing"));

      if (selectedComplexType?.value !== BIDDING_TYPES.JOINT) {
        if (creatingIndividualOrders) {
          const newCompletedCount = individualOrdersCompleted + 1;
          setIndividualOrdersCompleted(newCompletedCount);

          const correspondingOrderData = pendingOrderInvitations.find(
            (orderData) =>
              (orderData.material?.id || orderData.id) === res.material?.id
          );

          if (correspondingOrderData) {
            const emailInvitations = correspondingOrderData.suppliers.filter(
              (sup: any) => sup.emailsList && !sup.id
            );
            emailInvitations.forEach((invitation: any) => {
              createOrderInvitation(invitation.emailsList, res.id);
            });
          }

          if (newCompletedCount >= totalIndividualOrders) {
            setCreatingIndividualOrders(false);
            setIndividualOrdersCompleted(0);
            setTotalIndividualOrders(0);
            setPendingOrderInvitations([]);
            cleanUserInvites();
            return navigate("/orders");
          }
          return;
        }
      }

      return navigate("/orders");
    },
    (error) => {
      setIsSubmitting(false);
      openError(error);
    }
  );

  const { mutate: createMultipleOrder, isPending: isCreatingMultipleOrder } =
    useCreateMultipleOrder(
      (res) => {
        openNotification(i18next.t("tenders.processing"));
        orderInvitationHandler(true, res);
        return navigate("/orders");
      },
      (error) => {
        setIsSubmitting(false);
        openError(error);
      }
    );

  const { mutate: updateOrder, isPending: isUpdatingOrder } = useUpdateOrder(
    () => {
      openNotification(i18next.t("tenders.processing"));
      return navigate("/orders");
    },
    (error) => {
      setIsSubmitting(false);
      openError(error);
    }
  );

  const onSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    closeDialog();

    const dataToSubmit = buildRequestOrderPayload({
      ordersToSubmit,
      generalBidData,
      selectedType,
      selectedComplexType,
      user,
    });

    if (selectedComplexType.value === BIDDING_TYPES.JOINT) {
      createMultipleOrder(dataToSubmit);
    } else {
      if (dataToSubmit.length === 1) {
        const firstEntry = dataToSubmit[0];
        if (firstEntry.id) {
          // Edit mode: dataToSubmit[0].id is the existing order's ID
          // (sourced from `mat.orderId` via buildEditOrderEntry).
          updateOrder(firstEntry);
        } else {
          createOrder(firstEntry);
        }
      } else {
        setCreatingIndividualOrders(true);
        setIndividualOrdersCompleted(0);
        setTotalIndividualOrders(dataToSubmit.length);
        setPendingOrderInvitations(dataToSubmit);

        dataToSubmit.forEach((orderData) => {
          createOrder(orderData);
        });
      }
    }

    reset();
  };

  const onError = () => {
    setIsSubmitting(false);
    closeDialog();
  };

  return {
    isSubmitting,
    isCreatingOrder,
    isCreatingMultipleOrder,
    isUpdatingOrder,
    creatingIndividualOrders,
    onSubmit,
    onError,
  };
};
